import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import ArticleAd from "../ads/ArticleAd";
import { seoConfig } from "../data/seoConfig";
import { frenchHumanoidRobotsPage } from "../data/frenchHumanoidRobots";

const statusClasses = {
  active: "border-emerald-200 bg-emerald-50 text-emerald-800",
  research: "border-blue-200 bg-blue-50 text-blue-800",
  legacy: "border-amber-200 bg-amber-50 text-amber-800",
  historical: "border-neutral-300 bg-neutral-100 text-neutral-700",
  watchlist: "border-purple-200 bg-purple-50 text-purple-800"
};

function SectionHeader({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">{eyebrow}</p>
      <h2 className="mt-3 text-2xl font-black tracking-tight text-neutral-950 sm:text-3xl">{title}</h2>
      {description && <p className="mt-3 text-base leading-8 text-neutral-600">{description}</p>}
    </div>
  );
}

function SourceButton({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="focus-ring inline-flex items-center justify-center rounded-full border border-neutral-300 bg-white px-3 py-2 text-xs font-black text-neutral-900 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
    >
      {children}
    </a>
  );
}

function StatusBadge({ robot }) {
  return (
    <span className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-black ${statusClasses[robot.statusTone] || statusClasses.research}`}>
      {robot.status}
    </span>
  );
}

function FranceHubVisual() {
  const points = [
    { label: "Paris", x: "58%", y: "28%" },
    { label: "Bordeaux", x: "41%", y: "63%" },
    { label: "Toulouse", x: "50%", y: "78%" }
  ];

  return (
    <div className="relative min-h-[18rem] overflow-hidden rounded-[2rem] border border-white/80 bg-white/10 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.16)] backdrop-blur">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_45%_38%,rgba(255,255,255,0.42),transparent_8rem),radial-gradient(circle_at_42%_65%,rgba(34,197,94,0.35),transparent_8rem)]" />
      <div className="relative h-[18rem] rounded-[1.5rem] border border-white/30 bg-slate-950/35">
        <div className="absolute left-[28%] top-[13%] h-[14rem] w-[10rem] rounded-[48%_52%_42%_58%] border border-white/40 bg-white/10 shadow-inner" />
        <div className="absolute left-[36%] top-[27%] h-[10rem] w-[8rem] rotate-12 rounded-[45%_55%_50%_50%] border border-white/40 bg-white/10" />
        {points.map((point) => (
          <div key={point.label} className="absolute" style={{ left: point.x, top: point.y }}>
            <span className="absolute -left-2 -top-2 h-4 w-4 rounded-full border-2 border-white bg-emerald-400 shadow-[0_0_0_8px_rgba(52,211,153,0.22)]" />
            <span className="ml-4 inline-flex rounded-full bg-white px-3 py-1 text-xs font-black text-slate-950 shadow-lg">{point.label}</span>
          </div>
        ))}
      </div>
      <p className="relative mt-4 text-sm font-semibold leading-6 text-white/80">Paris, Bordeaux and Toulouse anchor the page because the robot evidence points to companies, labs and platforms in those cities.</p>
    </div>
  );
}

function RobotComparisonTable({ robots }) {
  return (
    <div className="overflow-hidden rounded-[1.5rem] border border-neutral-200 bg-white shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
      <div className="max-w-full overflow-x-auto overscroll-x-contain" role="region" aria-label="French humanoid robot comparison table" tabIndex={0}>
        <table className="min-w-[1040px] w-full border-collapse text-left text-sm">
          <caption className="sr-only">Comparison of French humanoid robots by company, city, status, use, mobility, hardware and source.</caption>
          <thead className="bg-slate-950 text-white">
            <tr>
              {["Robot", "Company / Lab", "City", "Status", "Main Use", "Mobility", "Key Hardware", "Source"].map((header) => (
                <th key={header} scope="col" className="px-4 py-4 text-[11px] font-black uppercase tracking-[0.12em]">{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {robots.map((robot, index) => (
              <tr key={robot.id} className={index % 2 === 0 ? "bg-white" : "bg-neutral-50"}>
                <th scope="row" className="px-4 py-4 align-top text-sm font-black text-neutral-950">{robot.name}</th>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.company}</td>
                <td className="px-4 py-4 align-top font-semibold text-neutral-800">🇫🇷 {robot.city}</td>
                <td className="px-4 py-4 align-top"><StatusBadge robot={robot} /></td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mainUse}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.mobility}</td>
                <td className="px-4 py-4 align-top text-neutral-700">{robot.keyHardware}</td>
                <td className="px-4 py-4 align-top"><SourceButton href={robot.sourceUrl}>Open source</SourceButton></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RobotCard({ robot }) {
  return (
    <article id={robot.id} className="soft-card p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-neutral-500">{robot.company}</p>
          <h3 className="mt-2 text-xl font-black tracking-tight text-neutral-950">{robot.name}</h3>
          <p className="mt-1 text-sm font-bold text-neutral-600">🇫🇷 {robot.city}</p>
        </div>
        <StatusBadge robot={robot} />
      </div>

      <div className="mt-5 grid gap-3 text-sm leading-6 text-neutral-700">
        <p><strong className="text-neutral-950">Main application</strong> {robot.mainUse}</p>
        <p><strong className="text-neutral-950">Technical notes</strong> {robot.keyHardware}</p>
        <p><strong className="text-neutral-950">Mobility</strong> {robot.mobility}</p>
        <p><strong className="text-neutral-950">Autonomy level</strong> {robot.autonomy}</p>
        <p><strong className="text-neutral-950">Why it matters</strong> {robot.whyItMatters}</p>
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <SourceButton href={robot.sourceUrl}>{robot.sourceLabel}</SourceButton>
        {robot.additionalSources?.map((source) => <SourceButton key={source.url} href={source.url}>{source.label}</SourceButton>)}
        <a href={`#${robot.id}`} className="focus-ring inline-flex rounded-full border border-neutral-200 px-3 py-2 text-xs font-black text-neutral-600 hover:border-blue-200 hover:text-blue-700">Section link</a>
      </div>
    </article>
  );
}

function ClassificationPanel({ groups }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {groups.map((group) => (
        <article key={group.label} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
          <h3 className="text-lg font-black text-neutral-950">{group.label}</h3>
          <ul className="mt-4 grid gap-2">
            {group.robots.map((robot) => (
              <li key={robot} className="rounded-2xl bg-neutral-50 px-4 py-3 text-sm font-bold text-neutral-700">{robot}</li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

export default function FrenchHumanoidRobots() {
  const page = frenchHumanoidRobotsPage;
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Humanoid Robots", to: "/robot-types/humanoid-robots" },
    { label: "France", to: page.path }
  ];

  const activeCount = page.robots.filter((robot) => ["active", "research"].includes(robot.statusTone)).length;
  const schema = [
    buildBreadcrumbSchema(breadcrumbs),
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: page.seoTitle,
      description: page.seoDescription,
      author: { "@type": "Organization", name: "TechniaHQRobot" },
      publisher: { "@type": "Organization", name: "TechniaHQRobot" },
      datePublished: "2026-07-08",
      dateModified: "2026-07-08",
      mainEntityOfPage: `${seoConfig.baseUrl}${page.path}`,
      about: page.robots.map((robot) => robot.name)
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "French humanoid robots",
      itemListElement: page.robots.map((robot, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: robot.name,
        url: `${seoConfig.baseUrl}${page.path}#${robot.id}`
      }))
    }
  ];

  return (
    <>
      <SEO title={page.seoTitle} description={page.seoDescription} path={page.path} type="article" schema={schema} />
      <Breadcrumbs items={breadcrumbs} />

      <section className="site-shell pb-8 pt-8 sm:pb-12 sm:pt-10 lg:pb-16">
        <div className="grid gap-6 rounded-[2rem] bg-slate-950 p-5 text-white shadow-[0_32px_90px_rgba(15,23,42,0.18)] sm:p-8 lg:grid-cols-[minmax(0,1fr)_26rem] lg:items-center lg:p-10">
          <div>
            <div className="flex flex-wrap gap-2">
              <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em]">🇫🇷 France robotics</span>
              <span className="rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.16em] text-emerald-100">Humanoid ecosystem</span>
            </div>
            <h1 className="mt-5 max-w-4xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">{page.title}</h1>
            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-200 sm:text-lg">
              France’s humanoid programmes include industrial development, open-source research, social robots and assistive systems. This page separates Wandercraft’s reported factory work with Calvin from the research use of Reachy 2 and the intended service applications of Mirokaï.
            </p>
            
            <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-300">
              <span>Updated {page.updatedAt}</span>
              <span>•</span>
              <span>{page.author}</span>
              <span>•</span>
              <span>{page.readingTime}</span>
            </div>
          </div>
          <FranceHubVisual />
        </div>
      </section>

      <nav className="site-shell sticky top-16 z-30 pb-4" aria-label="France humanoid page navigation">
        <div className="max-w-full overflow-x-auto rounded-full border border-neutral-200 bg-white/90 px-2 py-2 shadow-sm backdrop-blur">
          <div className="flex min-w-max gap-2">
            {[
              ["Summary", "summary"],
              ["Comparison", "comparison"],
              ["Robot cards", "robots"],
              ["France map", "map"],
              ["Timeline", "timeline"],
              ["Classification", "classification"],
              ["Sources", "sources"]
            ].map(([label, href]) => (
              <a key={href} href={`#${href}`} className="focus-ring rounded-full px-4 py-2 text-sm font-black text-neutral-700 hover:bg-blue-50 hover:text-blue-700">{label}</a>
            ))}
          </div>
        </div>
      </nav>

      <section className="site-shell pt-8">
        <ArticleAd placement="top" articleSlug="humanoid-robots-france" />
      </section>

      <section id="summary" className="site-shell grid gap-5 py-8 sm:grid-cols-2 lg:grid-cols-4">
        {page.summaryCards.map((card) => (
          <article key={card.label} className="soft-card p-5">
            <p className="text-3xl font-black tracking-tight text-neutral-950">{card.value}</p>
            <h2 className="mt-2 text-base font-black text-neutral-950">{card.label}</h2>
            <p className="mt-2 text-sm leading-6 text-neutral-600">{card.detail}</p>
          </article>
        ))}
      </section>

      <section id="comparison" className="site-shell py-10">
        <SectionHeader
          eyebrow="Comparison table"
          title="French humanoid robots, labs and platforms"
          description="This table separates active industrial systems, open-source research robots, service humanoids, legacy platforms and watchlist companies. Specs are included only when the source supports them."
        />
        <div className="mt-6">
          <RobotComparisonTable robots={page.robots} />
        </div>
      </section>

      <section id="robots" className="site-shell py-10">
        <SectionHeader
          eyebrow="Robot profiles"
          title="Detailed cards by robot"
          description="Each card lists status, location, application, hardware notes, autonomy limits and why the robot matters inside the French ecosystem."
        />
        <div className="mt-6 grid gap-5 lg:grid-cols-2">
          {page.robots.map((robot) => <RobotCard key={robot.id} robot={robot} />)}
        </div>
      </section>

      <section id="map" className="site-shell py-10">
        <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
          <div>
            <SectionHeader
              eyebrow="France map"
              title="Paris, Bordeaux and Toulouse"
              description="These locations group the companies and research platforms discussed in this article."
            />
            <div className="mt-6 rounded-[2rem] bg-slate-950 p-5 text-white">
              <FranceHubVisual />
            </div>
          </div>
          <div className="grid gap-4">
            {page.hubs.map((hub) => (
              <article key={hub.city} className="soft-card p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-xl font-black text-neutral-950">{hub.flag} {hub.city}</h3>
                  <span className="rounded-full border border-neutral-200 bg-neutral-50 px-3 py-1 text-xs font-black text-neutral-500">Hub</span>
                </div>
                <p className="mt-3 text-sm leading-6 text-neutral-600">{hub.detail}</p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {hub.robots.map((robot) => (
                    <span key={robot} className="rounded-full bg-blue-50 px-3 py-2 text-xs font-black text-blue-700">{robot}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="timeline" className="site-shell py-10">
        <SectionHeader
          eyebrow="Timeline"
          title="How the French humanoid story connects"
          description="The timeline is deliberately short it shows the ecosystem arc from education and social robots to open-source embodied AI and industrial humanoids."
        />
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          {page.timeline.map((item, index) => (
            <article key={item.label} className="rounded-[1.5rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)]">
              <p className="text-sm font-black text-blue-700">{String(index + 1).padStart(2, "0")}</p>
              <h3 className="mt-2 text-xl font-black text-neutral-950">{item.label}</h3>
              <p className="mt-3 text-sm leading-6 text-neutral-600">{item.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="classification" className="site-shell py-10">
        <SectionHeader
          eyebrow="Classification"
          title="Active, historical, research and watchlist"
          description={`The page counts ${activeCount} current or research-active entries, but it keeps legacy systems and watchlist companies clearly separated.`}
        />
        <div className="mt-6">
          <ClassificationPanel groups={page.classifications} />
        </div>
      </section>

      <section id="sources" className="site-shell py-10">
        <div className="rounded-[2rem] border border-neutral-200 bg-white p-5 shadow-[0_18px_60px_rgba(15,23,42,0.05)] sm:p-7">
          <SectionHeader
            eyebrow="Verification"
            title="Source links and claim limits"
            description="Every robot card links to the strongest available source used for the entry. Historical and watchlist platforms are labeled to avoid presenting them as current deployments."
          />
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {page.robots.map((robot) => (
              <SourceButton key={robot.id} href={robot.sourceUrl}>{robot.name}</SourceButton>
            ))}
          </div>
          <div className="mt-6 rounded-[1.5rem] bg-neutral-50 p-5 text-sm leading-7 text-neutral-600">
            <p><strong className="text-neutral-950">Editorial rule</strong> the page does not invent payloads, battery life, autonomy levels, deployment numbers or customer names. When a source uses broad claims, the page describes them as source-specific and avoids presenting them as universal capability.</p>
          </div>
        </div>
      </section>

      <section className="site-shell py-8">
        <div className="soft-card p-5 sm:p-6">
          <h2 className="text-xl font-black text-neutral-950">Related TechniaHQRobot guides</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/robot-types/humanoid-robots" className="btn-secondary">Humanoid robots guide</Link>
            <Link to="/robotics-companies" className="btn-secondary">Robotics companies</Link>
            <Link to="/robotics-data-collection" className="btn-secondary">Humanoid data collection</Link>
            <Link to="/robotics-news" className="btn-secondary">Robotics news</Link>
          </div>
        </div>
      </section>

      <section className="site-shell pb-8">
        <ArticleAd placement="bottom" articleSlug="humanoid-robots-france" />
      </section>

      <CTASection title="Follow TechniaHQRobot for verified humanoid robotics analysis" />
    </>
  );
}
